/**
 * LiveDiarizedTranscript - Real-time transcript viewer with WhisperLiveKit diarization
 * Shows speaker labels, timestamps, language detection, and lag indicators
 */
import { Box, Chip, Typography, Paper } from '@mui/material';
import { useEffect, useRef } from 'react';

export interface TranscriptLine {
  speaker: number;
  text: string;
  start?: number;
  end?: number;
  detected_language?: string;
  /** English translation (NLLW) when target_language is set; backend sends as translation_text or translation.text */
  translation_text?: string;
  translation?: { text?: string };
}

/** How to show source language vs translation in the transcript */
export type TranscriptDisplayMode = 'source' | 'translation' | 'both';

export interface LiveDiarizedTranscriptProps {
  lines: TranscriptLine[];
  bufferDiarization?: string;
  bufferTranscription?: string;
  bufferTranslation?: string;
  remainingTimeDiarization?: number;
  remainingTimeTranscription?: number;
  status?: string;
  isFinalizing?: boolean;
  /** Conversation layout: when true, odd speakers (1, 3, ...) align right, even (0, 2, ...) left. Default true for chat-like view. */
  alternateSpeakersLeftRight?: boolean;
  /** What to show: source only, translation only, or both. Default 'both'. */
  transcriptDisplayMode?: TranscriptDisplayMode;
}

// Color palette for different speakers
const SPEAKER_COLORS = [
  '#1976d2', // blue
  '#e91e63', // pink
  '#9c27b0', // purple
  '#ff9800', // orange
  '#4caf50', // green
  '#00bcd4', // cyan
];

const getSpeakerColor = (speakerNum: number): string => {
  if (speakerNum <= 0) return '#757575'; // grey for unknown
  return SPEAKER_COLORS[(speakerNum - 1) % SPEAKER_COLORS.length];
};

const formatTime = (seconds: number | undefined | null | string): string => {
  if (seconds === undefined || seconds === null) return '';
  const num = typeof seconds === 'string' ? parseFloat(seconds) : seconds;
  if (isNaN(num)) return '';
  return num.toFixed(1);
};

function getTranslationText(line: TranscriptLine): string {
  if (line.translation_text) return line.translation_text;
  if (line.translation && typeof line.translation === 'object' && line.translation.text)
    return line.translation.text;
  return '';
}

export default function LiveDiarizedTranscript({
  lines = [],
  bufferDiarization = '',
  bufferTranscription = '',
  bufferTranslation = '',
  remainingTimeDiarization = 0,
  remainingTimeTranscription = 0,
  status = 'active_transcription',
  isFinalizing = false,
  alternateSpeakersLeftRight = true,
  transcriptDisplayMode = 'both',
}: LiveDiarizedTranscriptProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new lines arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lines, bufferTranscription]);

  if (status === 'no_audio_detected') {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="body2" color="text.secondary" fontStyle="italic">
          No audio detected...
        </Typography>
      </Box>
    );
  }

  if (lines.length === 0) {
    const liveBuffer = bufferDiarization || bufferTranscription;
    if (!liveBuffer) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="text.secondary">
            Listening... Start speaking to see transcription
          </Typography>
        </Box>
      );
    }
    // WLK sends buffer_transcription while ASR is live but diarization hasn't assigned speakers yet.
    // Show it so text appears immediately rather than waiting for diarization to catch up.
    return (
      <Box sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 1, border: '1px solid #e0e0e0' }}>
        <Typography variant="body2" sx={{ color: '#757575', fontStyle: 'italic' }}>
          {liveBuffer}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={scrollRef}
      sx={{
        maxHeight: 600,
        overflow: 'auto',
        p: 2,
        bgcolor: '#fafafa',
        borderRadius: 1,
        border: '1px solid #e0e0e0',
      }}
    >
      {lines.map((line, index) => {
        const isLastLine = index === lines.length - 1;
        const speakerColor = getSpeakerColor(line.speaker);
        // Show Speaker 0 / Speaker 1 for all segments. Do NOT treat speaker 0 as "processing" — in 2-speaker
        // mode (e.g. Sarvam) both 0 and 1 are valid; showing "Processing diarization..." for 0 was incorrect.
        const showLoading = false;
        // Conversation layout: Speaker 0 left, Speaker 1 right (like chat). Odd speakers right, even left.
        const isRightAlign = alternateSpeakersLeftRight ? line.speaker % 2 === 1 : false;

        // Skip silence markers (-2)
        if (line.speaker === -2) {
          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'center',
                my: 1,
              }}
            >
              <Chip
                label={`Silence ${line.start !== undefined ? formatTime(line.start) : ''} - ${line.end !== undefined ? formatTime(line.end) : ''}`}
                size="small"
                sx={{
                  bgcolor: '#f5f5f5',
                  color: '#9e9e9e',
                  fontSize: '11px',
                }}
              />
            </Box>
          );
        }

        const translationText = getTranslationText(line);
        const hasTranslation = Boolean(translationText || (isLastLine && bufferTranslation));
        // Main text by display mode: source only, translation only, or source (translation shown separately)
        let displayText: string;
        if (transcriptDisplayMode === 'translation') {
          displayText = translationText || line.text || '';
          if (isLastLine && bufferTranslation) {
            displayText += isFinalizing ? ` ${bufferTranslation.trim()}` : ` ${bufferTranslation}`;
          }
        } else {
          displayText = line.text || '';
          if (isLastLine) {
            if (bufferDiarization) {
              displayText += isFinalizing
                ? ` ${bufferDiarization.trim()}`
                : ` ${bufferDiarization}`;
            }
            if (bufferTranscription) {
              displayText += isFinalizing
                ? ` ${bufferTranscription.trim()}`
                : ` ${bufferTranscription}`;
            }
          }
        }
        const showTranslationBlock =
          transcriptDisplayMode === 'both' && hasTranslation;

        return (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: isRightAlign ? 'flex-end' : 'flex-start',
              mb: 2,
            }}
          >
            {/* Speaker header with timestamp and language */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mb: 0.5,
              }}
            >
              {/* Speaker badge */}
              {showLoading ? (
                <Chip
                  label="Processing diarization..."
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '11px',
                    bgcolor: '#ffebee',
                    color: '#d32f2f',
                  }}
                />
              ) : (
                <Chip
                  label={`Speaker ${line.speaker}`}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '11px',
                    bgcolor: speakerColor,
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              )}

              {/* Timestamp */}
              {line.start !== undefined && line.end !== undefined && (
                <Typography
                  variant="caption"
                  sx={{ color: '#9e9e9e', fontSize: '10px' }}
                >
                  {formatTime(line.start)} - {formatTime(line.end)}
                </Typography>
              )}

              {/* Language detection */}
              {line.detected_language && (
                <Chip
                  label={line.detected_language.toUpperCase()}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '10px',
                    bgcolor: '#e3f2fd',
                    color: '#1976d2',
                  }}
                />
              )}
            </Box>

            {/* Message bubble */}
            <Paper
              elevation={1}
              sx={{
                p: 1.5,
                maxWidth: '75%',
                bgcolor: isRightAlign ? '#e3f2fd' : 'white',
                borderRadius: isRightAlign ? '12px 12px 4px 12px' : '12px 12px 12px 4px',
                borderLeft: isRightAlign ? 'none' : `4px solid ${speakerColor}`,
                borderRight: isRightAlign ? `4px solid ${speakerColor}` : 'none',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  lineHeight: 1.5,
                  color: '#333',
                  wordWrap: 'break-word',
                }}
              >
                {displayText}
                {!isFinalizing && bufferTranscription && isLastLine && (
                  <span style={{ color: '#9e9e9e', fontStyle: 'italic' }}>
                    {bufferTranscription}
                  </span>
                )}
              </Typography>
              {/* Translation block: only in 'both' mode so source + translation are clearly separated */}
              {showTranslationBlock && (
                <Typography
                  variant="body2"
                  sx={{
                    mt: 0.5,
                    pl: 0.5,
                    fontSize: '0.85rem',
                    color: '#666',
                    fontStyle: 'italic',
                    borderLeft: '2px solid #bdbdbd',
                  }}
                >
                  Translation: {translationText || (isLastLine ? bufferTranslation : '')}
                </Typography>
              )}

              {/* Lag indicators on last line */}
              {isLastLine && !isFinalizing && (
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {remainingTimeTranscription > 0 && (
                    <Chip
                      label={`Transcription lag: ${formatTime(remainingTimeTranscription)}s`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '10px',
                        bgcolor: '#fff3e0',
                        color: '#e65100',
                      }}
                    />
                  )}
                  {bufferDiarization && remainingTimeDiarization > 0 && (
                    <Chip
                      label={`Diarization lag: ${formatTime(remainingTimeDiarization)}s`}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: '10px',
                        bgcolor: '#f3e5f5',
                        color: '#4a148c',
                      }}
                    />
                  )}
                </Box>
              )}
            </Paper>
          </Box>
        );
      })}
    </Box>
  );
}
